// src/components/agents/agents-page.tsx
import { useCallback, useEffect, useState } from "react";
import {
  Workflow, ArrowRight,
  Plus, Play, Pencil, Trash2,
  AlertCircle,
  GitBranch,
} from "lucide-react";
import { ModuleHeader } from "../shell/module-header";
import { Agent, agentsApi } from "@/app/services/agents/agents-api";
import { AgentBuilder } from "./agent-builder";
import { AgentRunner } from "./agent-runner";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import { toast } from "sonner";
import { Button, EmptyState, Icon, IconButton, Spinner } from "../kit";
import { Orb } from "@/app/components/shared/advanced-vocabulary";
import { WORKFLOW_BLOCKS, edgeLabelFor, type BlockType } from "./workflow-blocks";
import "@/styles/advanced-tier.css";
import "@/styles/workflows-page.css";

// ── One workflow, previewed as its graph ─────────────────────────────────────
//
// Source: handoff-advanced §3.2 — "preview each workflow AS its graph".
//
// The card grid showed a truncated prompt, which tells you nothing about what
// a workflow DOES. A chain of block chips with their labelled connectors tells
// you exactly, and it is the same vocabulary the builder canvas uses, so the
// list and the editor are recognisably the same object.
//
// The outputs collapse into one "N outputs" chip and the chain WRAPS. A
// preview that scrolls sideways is a preview that hides half the workflow, and
// the handoff names the clipping as the thing to avoid.

/** The blocks a workflow actually has, derived from its own fields. */
function chainOf(agent: Agent): { setup: BlockType[]; outputs: BlockType[] } {
  const outputs: BlockType[] = [];
  if (agent.run_options.perception_score) outputs.push("perception_score");
  if (agent.run_options.enable_download) outputs.push("download_report");
  if (agent.run_options.enable_email) outputs.push("send_email");
  return { setup: ["brand_context", "analysis_prompt", "ai_models"], outputs };
}

function BlockChip({ type }: { type: BlockType }) {
  const def = WORKFLOW_BLOCKS[type];
  const Glyph = def.icon;
  return (
    <span
      className="upm-wf__chip"
      style={{ background: def.tint, borderColor: def.line, color: def.ink }}
    >
      <Glyph className="k-i k-i--sm" aria-hidden="true" />
      {def.label}
    </span>
  );
}

function FlowChain({ agent }: { agent: Agent }) {
  const { setup, outputs } = chainOf(agent);
  return (
    <div className="upm-wf__chain">
      {setup.map((t, i) => (
        <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-2)" }}>
          {i > 0 && (
            <span className="upm-wf__link">
              <ArrowRight className="k-i k-i--sm" aria-hidden="true" />
              {edgeLabelFor(setup[i - 1], t)}
            </span>
          )}
          <BlockChip type={t} />
        </span>
      ))}
      {outputs.length > 0 && (
        <>
          <span className="upm-wf__link">
            <ArrowRight className="k-i k-i--sm" aria-hidden="true" />
          </span>
          {/* One chip for the whole fan-out. The icons say WHICH, the count
              says how many, and neither has to fit on a line of its own. */}
          <span className="upm-wf__chip upm-wf__chip--outputs">
            {outputs.map((t) => {
              const Glyph = WORKFLOW_BLOCKS[t].icon;
              return (
                <Glyph
                  key={t}
                  className="k-i k-i--sm"
                  style={{ color: WORKFLOW_BLOCKS[t].ink }}
                  aria-hidden="true"
                />
              );
            })}
            {outputs.length} {outputs.length === 1 ? "output" : "outputs"}
          </span>
        </>
      )}
    </div>
  );
}

function WorkflowRow({
  agent, onRun, onEdit, onDelete,
}: {
  agent: Agent;
  onRun: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const blocks = chainOf(agent);
  const blockCount = blocks.setup.length + blocks.outputs.length;

  return (
    <article className="upm-wf__row">
      {/* The status spine. Idle here — this list has no run state on the
          record, and a spine claiming one would be inventing it. */}
      <span className="upm-wf__spine upm-wf__spine--idle" aria-hidden="true" />

      <div className="upm-wf__rowmain">
        <div className="upm-wf__rowhead">
          <h3 className="upm-wf__rowtitle">{agent.name}</h3>
          {agent.brand_name && <span className="upm-pub-status">{agent.brand_name}</span>}
          {agent.tags.map((t) => (
            <span key={t} className="upm-pub-status">{t}</span>
          ))}
        </div>

        <div className="upm-wf__rowmeta">
          <span>
            {blockCount} {blockCount === 1 ? "block" : "blocks"} ·{" "}
            {agent.selected_models.length}{" "}
            {agent.selected_models.length === 1 ? "model" : "models"}
          </span>
          <span className="upm-agent__convmeta" style={{ whiteSpace: "normal" }}>·</span>
          <span>Created {new Date(agent.created_at).toLocaleDateString()}</span>
        </div>

        {agent.description && <p className="upm-wf__rowdesc">{agent.description}</p>}

        <FlowChain agent={agent} />
      </div>

      <div className="upm-wf__rowactions">
        {/* Row-level actions are outline. Four solid teal buttons in a list
            leaves the eye no entry point, so Run is teal-OUTLINE and the one
            solid teal control on this page is the page-level "New workflow". */}
        <Button variant="secondary" icon={Play} onClick={onRun}>Run</Button>
        <Button variant="secondary" icon={Pencil} onClick={onEdit}>Edit flow</Button>
        <IconButton icon={Trash2} label={`Delete ${agent.name}`} onClick={onDelete} />
      </div>
    </article>
  );
}

interface AgentsPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  activeBrandName?: string;
}

export function AgentsPage({
  isSidebarCollapsed: _isSidebarCollapsed,
  onSidebarToggle: _onSidebarToggle,
  activeBrandName,
}: AgentsPageProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [builderAgent, setBuilderAgent] = useState<Agent | null | undefined>(undefined);
  const [runningAgent, setRunningAgent] = useState<Agent | null>(null);
  const [workflowPendingDelete, setWorkflowPendingDelete] = useState<Agent | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAgents(await agentsApi.list({ brandName: activeBrandName }));
    } catch (e: any) {
      setError(e.message || "Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, [activeBrandName]);

  useEffect(() => { load(); }, [load]);

  const requestDelete = (agent: Agent) => setWorkflowPendingDelete(agent);

  const confirmDelete = async () => {
    const agent = workflowPendingDelete;
    if (!agent) return;
    try {
      await agentsApi.delete(agent.id);
      setAgents((prev) => prev.filter((a) => a.id !== agent.id));
      toast.success("Workflow deleted");
    } catch (e: any) {
      toast.error(e.message || "Failed to delete workflow");
    } finally {
      setWorkflowPendingDelete(null);
    }
  };

  const handleSaved = (_saved: Agent) => {
    const wasEditing = builderAgent !== null;
    setBuilderAgent(undefined);
    load();
    toast.success(wasEditing ? "Workflow updated" : "Workflow created");
  };

  // ── Full-page builder mode ─────────────────────────────────────────────────
  if (builderAgent !== undefined) {
    return (
      <AgentBuilder
        agent={builderAgent}
        onClose={() => setBuilderAgent(undefined)}
        onSaved={handleSaved}
      />
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen upm-wf" data-kit>
      <div className="flex-1 min-w-0 overflow-auto">

        {/* Header (shared band) */}
        <ModuleHeader
          page="AI Workflows"
          icon={Workflow}
          title="Automate the probe, measure, act loop."
          description={
            activeBrandName
              ? `Visual workflows that run custom prompts against multiple AI models for ${activeBrandName}.`
              : "Build visual workflows that run custom prompts against multiple AI models, generate perception scores, and export results."
          }
          action={{
            label: "New workflow",
            icon: Plus,
            onClick: () => setBuilderAgent(null),
          }}
        />

        {/* Content */}
        <div className="px-4 md:px-8 py-6 md:py-8">

          {loading && <Spinner label="Loading workflows" />}

          {error && !loading && (
            <EmptyState icon={AlertCircle} title="Workflows could not be loaded" description={error} />
          )}

          {!loading && !error && agents.length === 0 && (
            <EmptyState
              icon={GitBranch}
              title={activeBrandName ? `No workflows for ${activeBrandName}` : "No workflows yet"}
              description="A workflow is three setup blocks — brand, prompt, models — wired to the outputs you want. Build the first one on the canvas."
              action={
                <Button icon={Plus} onClick={() => setBuilderAgent(null)}>
                  Build a workflow
                </Button>
              }
            />
          )}

          {!loading && !error && agents.length > 0 && (
            <div className="upm-wf__rows">
              {/* The violet create row: describe a workflow and the model
                  wires it up. It opens the builder, which is the honest
                  version of that promise until the wiring exists. */}
              <button type="button" className="upm-wf__create" onClick={() => setBuilderAgent(null)}>
                <Orb size="sm" />
                <span className="upm-wf__createcopy">
                  <span className="upm-wf__createtitle" style={{ display: "block" }}>
                    Build a new workflow
                  </span>
                  <span className="upm-wf__createsub">
                    Drag blocks onto a canvas and connect them. Three setup blocks and up to three outputs.
                  </span>
                </span>
                <Icon icon={Plus} size="md" />
              </button>

              {agents.map((a) => (
                <WorkflowRow
                  key={a.id}
                  agent={a}
                  onRun={() => setRunningAgent(a)}
                  onEdit={() => setBuilderAgent(a)}
                  onDelete={() => requestDelete(a)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Runner modal */}
      {runningAgent && (
        <AgentRunner
          agent={runningAgent}
          onClose={() => setRunningAgent(null)}
          onRunSaved={() => {}}
        />
      )}

      {/* Delete-workflow confirmation */}
      <ConfirmDialog
        open={workflowPendingDelete !== null}
        onOpenChange={(open) => { if (!open) setWorkflowPendingDelete(null); }}
        onConfirm={confirmDelete}
        title={workflowPendingDelete ? `Delete "${workflowPendingDelete.name}"?` : "Delete workflow?"}
        description="This will permanently delete the workflow. Past run results in Workflow History will be preserved, but the workflow definition itself cannot be recovered."
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}